/**
 * SUB-1.35 · F-64. Whether a signed-in identity is still allowed to hold a
 * session.
 *
 * ⚠️ **THIS IS NOT A SECURITY BOUNDARY AND MUST NEVER BE DESCRIBED AS ONE.**
 *
 * It runs in the browser. A deactivated user holding a valid refresh token can
 * call PostgREST directly and this file will never execute. What it does is stop
 * the product from behaving as though a deactivated account is a working one,
 * which is worth doing and is not the same thing as revoking access.
 *
 * The actual revocation is server-side and is the rest of SUB-1.35:
 *   - `check_project_access` tests `is_active` on no branch at all. Its
 *     `pa.is_active` is a property of the pipeline ASSIGNMENT, not of the user,
 *     which is exactly why this was missed for so long. 3 policies call it.
 *   - `user_has_role` tests `is_active` on its legacy `users.role_id` branch and
 *     NOT on its `user_roles` fallback, where SUB-2.6 puts every Agency Admin.
 *     32 policies call it.
 *   - ⚠️ `home_tenant_id` does not test it either, and is reached from
 *     `get_current_user_tenant_id`, which the codebase says is evaluated inside
 *     **641 policies**. Returning NULL there ends a deactivated user's session in
 *     one statement: the right outcome, and a very large blast radius.
 *
 * ---------------------------------------------------------------------------
 * MEASURED 2026-08-16, which is why this exists at all:
 *
 *   deactivated users                                    82
 *   of those, still holding an `auth.users` row      82 of 82
 *   of those, banned in `auth.users`                      0
 *   `is_active` gate anywhere in the auth or login path  none
 *
 * So today, deactivating somebody removes them from a list in the UI and takes
 * nothing away.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ IT FAILS OPEN, DELIBERATELY, AND HERE IS THE ARGUMENT.
 *
 * If the status cannot be read, this returns "allowed". Failing closed would
 * turn one slow query or one RLS change into every user being unable to sign in,
 * and this file is not what is standing between a deactivated user and the data:
 * the policies are. A UX gate that can lock out the whole platform is a worse
 * trade than one that occasionally lets a deactivated account reach a page that
 * will then show it nothing.
 *
 * ⚠️ That argument only holds while the server-side half is being done. If this
 * ever becomes the only check, it must be changed to fail closed, and this
 * paragraph is the reason to notice.
 */

/**
 * Where the sign-out reason is left for the login screen to pick up.
 *
 * ⚠️ `sessionStorage`, not `localStorage`: it is a one-time notice about this
 * tab's sign-out, not a preference to remember. In localStorage it would
 * reappear in every future tab until something cleared it.
 */
export const AUTH_NOTICE_KEY = 'authNotice';

/** The one row this decision needs. `null` means the read failed or returned nothing. */
export interface AccountStatusRow {
  is_active: boolean | null;
}

export interface SessionAllowed {
  allowed: true;
}
export interface SessionRefused {
  allowed: false;
  reason: string;
}
export type SessionDecision = SessionAllowed | SessionRefused;

/**
 * ⚠️ LOAD-BEARING COPY. What a deactivated person is told, and the only place the
 * product explains why it signed them out. `accountStatus.test.ts` asserts on it.
 *
 * It says what happened and who can undo it, and it does NOT say whether the
 * email exists, whether the password was right, or which account they belonged
 * to. Somebody probing with a stolen credential learns only that this particular
 * login is over.
 */
export const DEACTIVATED_MESSAGE =
  'This account has been deactivated. Contact your administrator if you think that is wrong.';

/**
 * May this identity hold a session?
 *
 * ⚠️ ONLY AN EXPLICIT `false` REFUSES, AND MY FIRST REASON FOR THAT WAS WRONG.
 * I wrote that `is_active` is nullable and that most rows were created without
 * it. **Measured 2026-08-16: the column is NOT NULL with `DEFAULT true`, and 0
 * of 548 rows are null.** So the null branch below is unreachable through the
 * database today.
 *
 * It stays, for two reasons that are true. The generated row type is nullable,
 * so a null can reach this function from the client even when the column cannot
 * produce one; and if the column is ever made nullable, the safe reading of a
 * null is "nobody has said otherwise" rather than "deactivated". The SQL half of
 * this story (`20260816d`) writes a bare `AND u.is_active` precisely BECAUSE the
 * column is NOT NULL, and its suite asserts that as row C5 so the two halves
 * cannot drift apart on it.
 */
export function decideSession(row: AccountStatusRow | null | undefined): SessionDecision {
  // The read failed, or returned no row. Fail open: see the header.
  if (row === null || row === undefined) return { allowed: true };

  if (row.is_active === false) {
    return { allowed: false, reason: DEACTIVATED_MESSAGE };
  }

  return { allowed: true };
}

/**
 * Whether a decision should end the session.
 *
 * ⚠️ A TYPE GUARD, NOT A BOOLEAN, AND THAT IS THE POINT. The caller needs the
 * `reason` to show the user, and a plain boolean leaves the union unnarrowed, so
 * reaching for `.reason` is a compile error and the temptation is to cast. The
 * guard means the only way to learn that a session is refused is to be handed
 * the reason along with it.
 */
export function shouldSignOut(decision: SessionDecision): decision is SessionRefused {
  return decision.allowed === false;
}
